import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  EnvelopeIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  StarIcon,
  RectangleStackIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useDashboardStats, useDashboardActivity, useDashboardCharts } from '../../hooks/useAPI';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [chartDays, setChartDays] = useState(30);
  
  // Fetch data
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: activities, isLoading: activitiesLoading } = useDashboardActivity(15);
  const { data: chartData, isLoading: chartLoading } = useDashboardCharts(chartDays);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'published': 'bg-green-100 text-green-800',
      'draft': 'bg-yellow-100 text-yellow-800',
      'new': 'bg-blue-100 text-blue-800',
      'unread': 'bg-red-100 text-red-800',
      'inactive': 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get icon color
  const getIconColor = (color) => {
    const colors = {
      'blue': 'bg-blue-500',
      'green': 'bg-green-500',
      'yellow': 'bg-yellow-500',
      'purple': 'bg-purple-500',
      'indigo': 'bg-indigo-500',
      'red': 'bg-red-500',
    };
    return colors[color] || 'bg-gray-500';
  };

  // Stat cards configuration
  const statCards = stats ? [
    {
      name: 'Services',
      value: stats.overview.services.total,
      subValue: `${stats.overview.services.active} active`,
      icon: CogIcon,
      color: 'bg-blue-500',
      link: '/admin/services'
    },
    {
      name: 'Blog Posts',
      value: stats.overview.blog.total,
      subValue: `${stats.overview.blog.published} published`,
      change: stats.overview.blog.growth,
      icon: DocumentTextIcon,
      color: 'bg-green-500',
      link: '/admin/blog'
    },
    {
      name: 'Active Jobs',
      value: stats.overview.jobs.active,
      subValue: `${stats.overview.applications.new} new applications`,
      change: stats.overview.jobs.growth,
      icon: BriefcaseIcon,
      color: 'bg-purple-500',
      link: '/admin/jobs'
    },
    {
      name: 'Messages',
      value: stats.overview.messages.total,
      subValue: `${stats.overview.messages.unread} unread`,
      change: stats.overview.messages.growth,
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-yellow-500',
      link: '/admin/messages'
    },
    {
      name: 'Newsletter',
      value: stats.overview.newsletter.active,
      subValue: `${stats.overview.newsletter.last7Days} new this week`,
      change: stats.overview.newsletter.growth,
      icon: EnvelopeIcon,
      color: 'bg-indigo-500',
      link: '/admin/newsletter'
    },
    {
      name: 'Portfolio',
      value: stats.overview.portfolio.total,
      subValue: `${stats.overview.portfolio.featured} featured`,
      icon: PhotoIcon,
      color: 'bg-pink-500',
      link: '/admin/portfolio'
    },
    {
      name: 'Team Members',
      value: stats.overview.team.total,
      subValue: 'Active members',
      icon: UsersIcon,
      color: 'bg-cyan-500',
      link: '/admin/team'
    },
    {
      name: 'Media Files',
      value: stats.overview.media.total,
      subValue: 'Total uploads',
      icon: RectangleStackIcon,
      color: 'bg-orange-500',
      link: '/admin/media'
    },
  ] : [];

  if (statsLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Welcome back! 👋
                </h2>
                <p className="mt-2 text-yellow-100">
                  Here's what's happening with your website today
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
                  <ClockIcon className="h-12 w-12 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <div 
              key={stat.name} 
              onClick={() => stat.link && navigate(stat.link)}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${stat.color}`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                        {stat.change !== undefined && (
                          <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                            stat.change >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {stat.change >= 0 ? (
                              <ArrowTrendingUpIcon className="self-center flex-shrink-0 h-4 w-4 mr-0.5" />
                            ) : (
                              <ArrowTrendingDownIcon className="self-center flex-shrink-0 h-4 w-4 mr-0.5" />
                            )}
                            <span>{Math.abs(stat.change)}%</span>
                          </div>
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-500">{stat.subValue}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Activity Overview</h3>
              <select 
                value={chartDays}
                onChange={(e) => setChartDays(Number(e.target.value))}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            {chartLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="blogPosts" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Blog Posts" />
                  <Area type="monotone" dataKey="jobs" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} name="Jobs" />
                  <Area type="monotone" dataKey="applications" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Applications" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Engagement Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Engagement</h3>
            {chartLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  />
                  <Legend />
                  <Bar dataKey="messages" fill="#eab308" name="Messages" />
                  <Bar dataKey="subscribers" fill="#6366f1" name="Subscribers" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            </div>
            {activitiesLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {activities && activities.length > 0 ? (
                  activities.map((activity) => (
                    <div key={activity.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${getIconColor(activity.color)}`}>
                            <div className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {activity.title}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {activity.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                            {activity.status}
                          </span>
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            {formatDate(activity.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-12 text-center text-gray-500">
                    No recent activity
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              <button 
                onClick={() => navigate('/admin/services')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <CogIcon className="h-5 w-5 mr-2" />
                Manage Services
              </button>
              <button 
                onClick={() => navigate('/admin/blog')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Manage Blog Posts
              </button>
              <button 
                onClick={() => navigate('/admin/jobs')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              >
                <BriefcaseIcon className="h-5 w-5 mr-2" />
                Manage Jobs
              </button>
              <button 
                onClick={() => navigate('/admin/team')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-colors"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                Manage Team
              </button>
              <button 
                onClick={() => navigate('/admin/portfolio')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors"
              >
                <PhotoIcon className="h-5 w-5 mr-2" />
                Manage Portfolio
              </button>
              <button 
                onClick={() => navigate('/admin/media')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
              >
                <RectangleStackIcon className="h-5 w-5 mr-2" />
                Media Library
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;